import React, { useState, useEffect, useMemo, memo } from 'react';
import { Popover, PopoverBody, UncontrolledTooltip } from 'reactstrap';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import _get from 'lodash/get';

export const blink = keyframes`
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
`;

const ProctorMonitorIcon = styled.span`
  i {
    color: #ff0000;
    padding-bottom: ${props => (props.isSingleProblemView ? 0 : '1.2rem')};
    padding-top: ${props => (props.isSingleProblemView ? 0 : '1.2rem')};
    font-size: 0.9rem;
    font-weight: 400;
    animation-name: ${blink};
    animation-duration: 1s;
    animation-iteration-count: infinite;
  }
`;

const ProctorMonitorPopover = styled(Popover)`
  z-index: 1200;
`;

function ProctorMonitorDropDown({ isSingleProblemView, id }) {
  const [proctorMonitorDropDownOpen, setProctorMonitorDropDownOpen] = useState(
    false
  );

  const PROCTOR_MONITOR_IN_MILLISECONDS = 1800000; // half an hour / 30 minutes

  const testSolutionSet = useSelector(state => state.testSolutionSet);

  const toggleProctorMonitorDropDown = () =>
    setProctorMonitorDropDownOpen(!proctorMonitorDropDownOpen);

  const { imageProctoring, videoProctoring } = useMemo(() => {
    return {
      imageProctoring: _get(testSolutionSet, 'settings.proctor.snapshot'),
      videoProctoring: _get(testSolutionSet, 'settings.proctor.video.enabled'),
    };
  }, [testSolutionSet]);

  // Memorize the popover placement based on isSingleProblemView
  const proctorMonitorPopoverPlacement = useMemo(() => {
    if (isSingleProblemView) {
      return 'left';
    }

    return 'bottom';
  });

  const closeProctorMonitorDropDown = () => {
    setProctorMonitorDropDownOpen(false);
  };

  /* Can't perform a React state update on an unmounted component. This is a no-op, but it indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function. 

  FIX: Return function with cancel handler  
  */

  // This useEffect, setInterval will be taken care.
  useEffect(() => {
    let proctorMonitorInterval;
    if (imageProctoring || videoProctoring) {
      proctorMonitorInterval = setInterval(() => {
        setProctorMonitorDropDownOpen(true);
      }, PROCTOR_MONITOR_IN_MILLISECONDS);
    }
    return () => {
      clearInterval(proctorMonitorInterval);
    };
  }, [imageProctoring, videoProctoring]);

  if (imageProctoring || videoProctoring) {
    return (
      <>
        <ProctorMonitorIcon
          isSingleProblemView={isSingleProblemView}
          id={id}
          type="button"
        >
          <i className="fa fa-circle" aria-hidden="true" />
        </ProctorMonitorIcon>

        <ProctorMonitorPopover
          trigger="click"
          placement={proctorMonitorPopoverPlacement}
          isOpen={proctorMonitorDropDownOpen}
          target={id}
          toggle={toggleProctorMonitorDropDown}
        >
          <PopoverBody>
            <div className="d-flex flex-row-reverse bd-highlight">
              <i
                className="fa fa-times cursor-pointer pr-2"
                aria-hidden="true"
                onClick={closeProctorMonitorDropDown}
                id="proctorMonitorPopoverClose"
              />
              <UncontrolledTooltip
                placement="top"
                target="proctorMonitorPopoverClose"
              >
                Close
              </UncontrolledTooltip>
            </div>
            <strong>Reminder:</strong>&nbsp;You are being watched. Ensure that{' '}
            your complete face is captured in the camera.
          </PopoverBody>
        </ProctorMonitorPopover>
      </>
    );
  }
}

export default memo(ProctorMonitorDropDown);
